import { api, type Connector, type IntrospectResponse } from '@/api/client'
import { aiApi } from '@/api/ai'
import { trutoAccountDisplayLabel } from '@/lib/truto-explorer-resource'
import { isIntegratedAccountExplorerType } from '@/lib/integrated-account-explorer'
import type { ChatReference } from '@/lib/resource-links'

/**
 * Active @-mention at the end of the input, mirroring slashMenuQuery (which
 * only matches a leading "/"). Mentions can start anywhere as long as the "@"
 * follows whitespace (or starts the input) and the query runs to the end —
 * matching on the whole value is the accepted v1 simplification for a plain
 * textarea (no caret tracking).
 */
const MENTION_RE = /(?:^|\s)@([\w./:-]*)$/

export function mentionQuery(text: string): string | null {
  const m = MENTION_RE.exec(text)
  return m ? m[1] : null
}

/** Remove the trailing `@query` after a mention is picked (chips are the
 * single source of truth — no inline token is left in the text). */
export function stripMentionQuery(text: string): string {
  return mentionQuery(text) !== null ? text.replace(/@[\w./:-]*$/, '') : text
}

export type MentionItem = {
  ref: ChatReference
  /** Secondary line in the menu (entity type / account id). */
  detail: string
}

// Search index doc types that are valid chat reference types (api_operation is
// searchable but not referenceable).
const MENTIONABLE_TYPES = new Set<ChatReference['type']>([
  'task', 'job', 'job_template', 'connector', 'trigger', 'dashboard', 'run', 'job_run', 'reference',
])

// One page of integrated-account explorer connectors, cached per session for the
// `<connector>:<account>` mention stage. Connector registries are small and a
// picker must not mutate the shared Pinia list (pagination rule), so this is a
// local non-mutating fetch.
let explorerConnectorsPromise: Promise<Connector[]> | null = null

function loadExplorerConnectors(): Promise<Connector[]> {
  explorerConnectorsPromise ??= api
    .get<{ result: Connector[] }>('/connectors?limit=100')
    .then(res => res.result.filter(c => isIntegratedAccountExplorerType(c.type)))
    .catch(() => {
      explorerConnectorsPromise = null
      return []
    })
  return explorerConnectorsPromise
}

function matchExplorerConnector(connectors: Connector[], prefix: string): Connector | null {
  if (!prefix) return null
  const p = prefix.toLowerCase()
  return (
    connectors.find(c => {
      const name = c.name.toLowerCase()
      return name.startsWith(p) || name.replace(/\s+/g, '-').startsWith(p)
    }) ?? null
  )
}

/**
 * Resolve mention menu items for a query. Two stages:
 * - `<connector>:<rest>` where `<connector>` prefix-matches an integrated-account
 *   explorer connector name → server-side integrated-account search on that connector.
 * - anything else → entity search across tasks/jobs/connectors/… via the
 *   existing /ai/search endpoint.
 */
export async function searchMentions(query: string): Promise<MentionItem[]> {
  const colon = query.indexOf(':')
  if (colon > 0) {
    const connectors = await loadExplorerConnectors()
    const connector = matchExplorerConnector(connectors, query.slice(0, colon))
    if (connector) {
      const rest = query.slice(colon + 1)
      const res = await api.post<IntrospectResponse>(`/connectors/${connector.id}/introspect`, {
        limit: 20,
        q: rest || undefined,
      })
      return (res.accounts ?? [])
        .filter(a => a.status == null || a.status === 'active')
        .map(a => ({
          ref: {
            type: 'account' as const,
            id: a.id,
            label: trutoAccountDisplayLabel(a),
            connector_id: connector.id,
            ...(a.integration_name ? { integration: a.integration_name } : {}),
          },
          detail: [connector.name, a.integration_name, a.id].filter(Boolean).join(' · '),
        }))
    }
  }

  if (!query.trim()) return []
  const res = await aiApi.search(query.trim())
  return res.results
    .filter(r => MENTIONABLE_TYPES.has(r.type as ChatReference['type']))
    .map(r => ({
      ref: {
        type: r.type as ChatReference['type'],
        id: String(r.resource_ref.id),
        label: r.resource_ref.label ?? r.name,
      },
      detail: r.description ? `${r.type} · ${r.description}` : r.type,
    }))
}
